import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Tabs,
  Tab,
  Grid,
  Checkbox,
  ListItemText,
  OutlinedInput,
  FormControlLabel,
  InputAdornment,
  Toolbar,
  TablePagination,
  TableSortLabel,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material'
import { PageContainer, StyledCard, SectionHeader, PrimaryButton } from '../components/ui/StyledComponents'
import supabase from '../supabaseClient'
import { useAuth } from '../context/AuthContext'
import { format } from 'date-fns'
import CreateGuardianForm from '../components/admin/CreateGuardianForm'
import WaitlistConversionModal from '../components/WaitlistConversionModal'
import { shiftOptions } from '../constants/shiftData'

function WaitlistPage() {
  const { userRole } = useAuth()
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [searchParams] = useSearchParams()
  const studentIdFromUrl = searchParams.get('student')
  const [waitlistEntries, setWaitlistEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  // Load filters from localStorage on mount
  const [statusFilter, setStatusFilter] = useState(() => {
    const saved = localStorage.getItem('waitlist_statusFilter')
    return saved || 'all'
  })
  const [shiftFilter, setShiftFilter] = useState(() => {
    const saved = localStorage.getItem('waitlist_shiftFilter')
    return saved || 'all'
  })
  const [phoneSearch, setPhoneSearch] = useState('')
  // Pagination state
  const [page, setPage] = useState(() => {
    const saved = localStorage.getItem('waitlist_page')
    return saved ? parseInt(saved, 10) : 0
  })
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const saved = localStorage.getItem('waitlist_rowsPerPage')
    return saved ? parseInt(saved, 10) : 25
  })
  // Sorting state - order can be 'asc', 'desc', or null (unsorted)
  const [orderBy, setOrderBy] = useState(() => {
    const saved = localStorage.getItem('waitlist_orderBy')
    return saved || 'created_at'
  })
  const [order, setOrder] = useState(() => {
    const saved = localStorage.getItem('waitlist_order')
    // Handle null string from localStorage
    if (saved === 'null' || saved === '') return null
    return saved || 'desc'
  })
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [selectedStudent, setSelectedStudent] = useState(null)
  const [students, setStudents] = useState([])
  const [desiredShift, setDesiredShift] = useState('')
  const [loadingStudents, setLoadingStudents] = useState(false)
  const [dialogTab, setDialogTab] = useState(0) // 0 = create new, 1 = select existing
  const [conversionModalOpen, setConversionModalOpen] = useState(false)
  const [pendingConversionEntry, setPendingConversionEntry] = useState(null)
  // New student form state - now supports multiple students
  const emptyStudent = {
    first_name: '',
    middle_name: '',
    last_name: '',
    gender: '',
    date_of_birth: '',
    is_quran_student: true,
    is_greenbook_student: false,
    quran_completed: false,
    desired_shifts: [],
  }
  const [newStudents, setNewStudents] = useState([{ ...emptyStudent }])
  const [guardians, setGuardians] = useState([])
  const [selectedGuardian, setSelectedGuardian] = useState(null)
  const [guardianMode, setGuardianMode] = useState('create') // 'select' or 'create'
  const [newlyCreatedGuardianId, setNewlyCreatedGuardianId] = useState(null)
  const [creatingStudent, setCreatingStudent] = useState(false)

  // Helper function to format phone number
  const formatPhoneNumber = (phone) => {
    if (!phone) return 'N/A'
    const digits = phone.replace(/\D/g, '')
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
    }
    return phone
  }

  const isAdmin = userRole === 'admin' || userRole === 'super_admin'

  // Save filters to localStorage when they change
  useEffect(() => {
    localStorage.setItem('waitlist_statusFilter', statusFilter)
  }, [statusFilter])

  useEffect(() => {
    localStorage.setItem('waitlist_shiftFilter', shiftFilter)
  }, [shiftFilter])

  // Save pagination preferences to localStorage
  useEffect(() => {
    localStorage.setItem('waitlist_page', page.toString())
  }, [page])

  useEffect(() => {
    localStorage.setItem('waitlist_rowsPerPage', rowsPerPage.toString())
  }, [rowsPerPage])

  useEffect(() => {
    localStorage.setItem('waitlist_orderBy', orderBy)
  }, [orderBy])

  useEffect(() => {
    // Store null as string 'null' for localStorage
    localStorage.setItem('waitlist_order', order === null ? 'null' : order)
  }, [order])

  useEffect(() => {
    if (isAdmin) {
      fetchWaitlistEntries()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, statusFilter, shiftFilter, studentIdFromUrl])

  useEffect(() => {
    // If student ID is in URL, fetch students
    if (studentIdFromUrl && isAdmin && students.length === 0) {
      fetchStudents()
    }
    // If add dialog opens, fetch students and guardians if not already loaded
    if (addDialogOpen && isAdmin) {
      if (students.length === 0) {
        fetchStudents()
      }
      if (guardians.length === 0) {
        fetchGuardians()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentIdFromUrl, isAdmin, addDialogOpen])

  useEffect(() => {
    // Once students are loaded and student ID is in URL, pre-select and open dialog
    if (studentIdFromUrl && students.length > 0 && !selectedStudent) {
      const student = students.find(s => s.id === studentIdFromUrl)
      if (student) {
        setSelectedStudent(student)
        setAddDialogOpen(true)
        setDialogTab(1) // Set to select existing tab
      }
    }
  }, [studentIdFromUrl, students, selectedStudent])

  // Handle newly created guardian selection
  useEffect(() => {
    if (newlyCreatedGuardianId && guardians.length > 0) {
      const guardian = guardians.find(g => g.id === newlyCreatedGuardianId)
      if (guardian) {
        setSelectedGuardian(guardian)
        setGuardianMode('select')
        setNewlyCreatedGuardianId(null)
      }
    }
  }, [newlyCreatedGuardianId, guardians])

  const fetchStudents = async () => {
    try {
      setLoadingStudents(true)
      const { data, error } = await supabase
        .from('students')
        .select('id, first_name, last_name')
        .eq('is_active', true)
        .order('last_name')

      if (error) throw error
      setStudents(data || [])
      
      // Note: Student selection from URL is handled in separate useEffect
    } catch (error) {
      console.error('Error fetching students:', error)
    } finally {
      setLoadingStudents(false)
    }
  }

  const fetchGuardians = async () => {
    try {
      const { data, error } = await supabase
        .from('guardians')
        .select('id, first_name, last_name')
        .eq('is_active', true)
        .order('last_name')

      if (error) throw error
      setGuardians(data || [])
    } catch (error) {
      console.error('Error fetching guardians:', error)
    }
  }

  const fetchWaitlistEntries = async () => {
    try {
      setLoading(true)
      setError('')

      let query = supabase
        .from('waitlist')
        .select(`
          *,
          students (
            id,
            first_name,
            last_name,
            is_active
          ),
          guardians (
            id,
            first_name,
            last_name,
            phone
          )
        `)
        .order('created_at', { ascending: false })

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter)
      }

      // Filter by student if provided in URL
      if (studentIdFromUrl) {
        query = query.eq('student_id', studentIdFromUrl)
      }

      const { data, error: fetchError } = await query

      if (fetchError) throw fetchError
      
      // Filter by shift client-side (since array filtering with OR can be complex)
      let filteredData = data || []
      if (shiftFilter !== 'all') {
        filteredData = filteredData.filter((entry) => {
          const shifts = entry.desired_shifts || (entry.desired_shift ? [entry.desired_shift] : [])
          return shifts.includes(shiftFilter)
        })
      }
      
      setWaitlistEntries(filteredData)
    } catch (error) {
      console.error('Error fetching waitlist entries:', error)
      setError('Failed to load waitlist entries')
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateStatus = async (entryId, newStatus) => {
    if (newStatus === 'accepted') {
      // Fetch full entry details
      try {
        const { data: entry, error: fetchError } = await supabase
          .from('waitlist')
          .select('*')
          .eq('id', entryId)
          .single()

        if (fetchError) {
          setError('Failed to fetch waitlist entry')
          return
        }

        // Check if student already exists
        if (entry.student_id) {
          // Existing student - still show conversion modal to assign/change classes
          // But we'll update the conversion handler to handle existing students differently
          setPendingConversionEntry(entry)
          setConversionModalOpen(true)
        } else {
          // New prospect - open conversion modal
          setPendingConversionEntry(entry)
          setConversionModalOpen(true)
        }
      } catch (error) {
        console.error('Error updating status:', error)
        setError('Failed to update status')
      }
    } else {
      // For other statuses, just update
      try {
        const { error } = await supabase
          .from('waitlist')
          .update({ status: newStatus })
          .eq('id', entryId)

        if (error) throw error

        setSuccess(`Status updated to ${newStatus}`)
        fetchWaitlistEntries()
        setTimeout(() => setSuccess(''), 3000)
      } catch (error) {
        console.error('Error updating status:', error)
        setError('Failed to update status')
      }
    }
  }

  const handleRemove = async (entryId) => {
    try {
      const { error } = await supabase
        .from('waitlist')
        .update({ status: 'removed' })
        .eq('id', entryId)

      if (error) throw error

      setSuccess('Waitlist entry removed')
      fetchWaitlistEntries()
      setTimeout(() => setSuccess(''), 3000)
    } catch (error) {
      console.error('Error removing entry:', error)
      setError('Failed to remove entry')
    }
  }

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'warning'
      case 'accepted':
        return 'success'
      case 'rejected':
        return 'error'
      case 'removed':
        return 'default'
      default:
        return 'default'
    }
  }

  const handleAddToWaitlist = async () => {
    if (!selectedStudent || !desiredShift.trim()) {
      setError('Please select a student and desired shift')
      return
    }

    try {
      setError('')
      const { error: insertError } = await supabase
        .from('waitlist')
        .insert({
          student_id: selectedStudent.id,
          desired_shift: desiredShift,
          desired_shifts: [desiredShift], // Also store as array for consistency
          status: 'pending',
        })

      if (insertError) throw insertError

      setSuccess('Student added to waitlist successfully')
      setAddDialogOpen(false)
      setDesiredShift('')
      setSelectedStudent(null)
      setDialogTab(0)
      fetchWaitlistEntries()
      fetchStudents() // Refresh students list
      
      // Clear URL param if it exists
      if (studentIdFromUrl) {
        navigate('/admin/waitlist', { replace: true })
      }
      
      setTimeout(() => setSuccess(''), 3000)
    } catch (error) {
      console.error('Error adding to waitlist:', error)
      setError('Failed to add student to waitlist')
    }
  }

  const handleGuardianCreated = async () => {
    // Refresh guardians list after creation
    await fetchGuardians()
    // The newly created guardian will be selected via useEffect
  }

  const handleStudentChange = (idx, field, value) => {
    setNewStudents((prev) =>
      prev.map((student, i) =>
        i === idx ? { ...student, [field]: value } : student
      )
    )
  }

  const addStudent = () => {
    setNewStudents((prev) => [...prev, { ...emptyStudent }])
  }

  const removeStudent = (idx) => {
    if (newStudents.length > 1) {
      setNewStudents((prev) => prev.filter((_, i) => i !== idx))
    }
  }

  const handleCreateStudentAndAddToWaitlist = async () => {
    // Validate guardian
    if (!selectedGuardian) {
      setError('Please select or create a guardian')
      return
    }

    // Validate all students
    for (const student of newStudents) {
      if (!student.first_name.trim() || !student.last_name.trim()) {
        setError('First name and last name are required for all students')
        return
      }
      if (student.desired_shifts.length === 0) {
        setError('Please select at least one desired shift for each student')
        return
      }
    }

    try {
      setCreatingStudent(true)
      setError('')

      // Create waitlist entries for each student
      const waitlistEntries = newStudents.map((student) => ({
        guardian_id: selectedGuardian.id,
        first_name: student.first_name.trim(),
        middle_name: student.middle_name.trim() || null,
        last_name: student.last_name.trim(),
        gender: student.gender || null,
        date_of_birth: student.date_of_birth || null,
        is_quran_student: student.is_quran_student,
        is_greenbook_student: student.is_greenbook_student,
        quran_completed: student.quran_completed,
        desired_shifts: student.desired_shifts,
        desired_shift: student.desired_shifts[0] || null, // Keep for backward compatibility
        status: 'pending',
        student_id: null, // Will be set on conversion
      }))

      const { error: waitlistError } = await supabase
        .from('waitlist')
        .insert(waitlistEntries)

      if (waitlistError) throw waitlistError

      setSuccess(
        newStudents.length === 1
          ? 'Prospect added to waitlist successfully'
          : `${newStudents.length} prospects added to waitlist successfully`
      )
      setAddDialogOpen(false)
      // Reset form
      setNewStudents([{ ...emptyStudent }])
      setSelectedGuardian(null)
      setGuardianMode('create')
      fetchWaitlistEntries()
      
      setTimeout(() => setSuccess(''), 3000)
    } catch (error) {
      console.error('Error adding to waitlist:', error)
      setError(error.message || 'Failed to add prospect to waitlist')
    } finally {
      setCreatingStudent(false)
    }
  }

  const handleConvertWaitlistEntry = async (entry, selectedClassIds) => {
    try {
      setLoading(true)
      setError('')

      let studentId = entry.student_id

      // If student doesn't exist, create it
      if (!studentId) {
        // 1. Create student from waitlist data
        const { data: newStudent, error: studentError } = await supabase
          .from('students')
          .insert({
            first_name: entry.first_name,
            middle_name: entry.middle_name || null,
            last_name: entry.last_name,
            gender: entry.gender || null,
            date_of_birth: entry.date_of_birth || null,
            class_ids: selectedClassIds, // Assign to selected classes
            is_quran_student: entry.is_quran_student ?? true,
            is_greenbook_student: entry.is_greenbook_student ?? false,
            quran_completed: entry.quran_completed ?? false,
          })
          .select()
          .single()

        if (studentError) throw studentError
        studentId = newStudent.id

        // 2. Create guardian relationship (only for new students)
        if (entry.guardian_id) {
          const { error: relationshipError } = await supabase
            .from('student_guardians')
            .insert({
              student_id: studentId,
              guardian_id: entry.guardian_id,
              relationship_type: 'parent',
              is_primary: true,
            })

          if (relationshipError) throw relationshipError
        }
      } else {
        // Existing student - update class assignments
        const { data: existingStudent, error: fetchError } = await supabase
          .from('students')
          .select('class_ids')
          .eq('id', studentId)
          .single()

        if (fetchError) throw fetchError

        // Merge with existing classes (avoid duplicates)
        const updatedClassIds = [...new Set([...(existingStudent.class_ids || []), ...selectedClassIds])]

        const { error: updateStudentError } = await supabase
          .from('students')
          .update({ class_ids: updatedClassIds })
          .eq('id', studentId)

        if (updateStudentError) throw updateStudentError
      }

      // 3. Update waitlist entry
      const { error: updateError } = await supabase
        .from('waitlist')
        .update({
          status: 'accepted',
          student_id: studentId,
          converted_at: new Date().toISOString(),
        })
        .eq('id', entry.id)

      if (updateError) throw updateError

      setSuccess(entry.student_id 
        ? 'Student assigned to classes successfully'
        : 'Student created and assigned to classes successfully')
      setConversionModalOpen(false)
      setPendingConversionEntry(null)
      fetchWaitlistEntries()
      
      setTimeout(() => setSuccess(''), 3000)
    } catch (error) {
      console.error('Error converting waitlist entry:', error)
      setError(error.message || 'Failed to convert waitlist entry')
    } finally {
      setLoading(false)
    }
  }

  const handleDialogClose = () => {
    setAddDialogOpen(false)
    setDesiredShift('')
    setSelectedStudent(null)
    setDialogTab(0)
    // Reset new student form
    setNewStudents([{ ...emptyStudent }])
    setSelectedGuardian(null)
    setGuardianMode('create')
    if (studentIdFromUrl) {
      navigate('/admin/waitlist', { replace: true })
    }
  }

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0) // Reset to first page when changing rows per page
  }

  // Sorting handler - cycles through: asc → desc → null (unsorted)
  const handleRequestSort = (property) => {
    if (orderBy === property) {
      // Same column - cycle through states: asc → desc → null → asc
      if (order === 'asc') {
        setOrder('desc')
      } else if (order === 'desc') {
        setOrder(null) // Unsorted - keep orderBy as the column but don't sort
      } else {
        // order is null - start ascending again
        setOrder('asc')
      }
    } else {
      // Different column - start with ascending
      setOrderBy(property)
      setOrder('asc')
    }
    setPage(0) // Reset to first page when sorting changes
  }

  // Reset page when filters change
  useEffect(() => {
    setPage(0)
  }, [statusFilter, shiftFilter, phoneSearch])

  // Filter entries by phone search
  const filteredEntries = waitlistEntries.filter((entry) => {
    if (!phoneSearch.trim()) return true
    
    const phone = entry.guardians?.phone || ''
    // Remove all non-digit characters for comparison
    const phoneDigits = phone.replace(/\D/g, '')
    const searchDigits = phoneSearch.replace(/\D/g, '')
    
    // Match if search digits are found anywhere in the phone number
    return phoneDigits.includes(searchDigits)
  })

  // Sort filtered entries (only if order is not null)
  const sortedEntries = order === null 
    ? [...filteredEntries] // No sorting when order is null
    : [...filteredEntries].sort((a, b) => {
      let aValue, bValue
      
      switch (orderBy) {
      case 'student_name':
        aValue = a.student_id && a.students
          ? `${a.students.first_name} ${a.students.last_name}`.toLowerCase()
          : `${a.first_name || ''} ${a.last_name || ''}`.trim().toLowerCase()
        bValue = b.student_id && b.students
          ? `${b.students.first_name} ${b.students.last_name}`.toLowerCase()
          : `${b.first_name || ''} ${b.last_name || ''}`.trim().toLowerCase()
        break
      case 'guardian_name':
        aValue = a.guardians 
          ? `${a.guardians.first_name} ${a.guardians.last_name}`.toLowerCase()
          : ''
        bValue = b.guardians 
          ? `${b.guardians.first_name} ${b.guardians.last_name}`.toLowerCase()
          : ''
        break
      case 'guardian_phone':
        aValue = a.guardians?.phone || ''
        bValue = b.guardians?.phone || ''
        break
      case 'desired_shift':
        // Sort by first shift in the array, or single shift value
        const aShifts = a.desired_shifts || (a.desired_shift ? [a.desired_shift] : [])
        const bShifts = b.desired_shifts || (b.desired_shift ? [b.desired_shift] : [])
        aValue = aShifts.length > 0 ? aShifts[0].toLowerCase() : ''
        bValue = bShifts.length > 0 ? bShifts[0].toLowerCase() : ''
        break
      case 'status':
        aValue = a.status || ''
        bValue = b.status || ''
        break
      case 'created_at':
      default:
        aValue = new Date(a.created_at).getTime()
        bValue = new Date(b.created_at).getTime()
    }
    
      if (order === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0
      }
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0
    })

  // Paginate the sorted entries
  const paginatedEntries = sortedEntries.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  )

  if (!isAdmin) {
    return (
      <PageContainer>
        <Alert severity="error">You do not have permission to access this page.</Alert>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: { xs: 2, sm: 3 },
        gap: { xs: 2, sm: 0 },
      }}>
        <SectionHeader sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }, mb: 0 }}>
          Waitlist Management
        </SectionHeader>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <StyledCard>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            <Toolbar
              sx={{
                pl: { xs: 1, sm: 2 },
                pr: { xs: 1, sm: 1 },
                py: { xs: 1, sm: 0 },
                borderBottom: 1,
                borderColor: 'divider',
                flexWrap: 'wrap',
                gap: { xs: 1.5, sm: 2 },
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
              }}
            >
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 }, width: { xs: '100%', sm: 'auto' } }}>
                <InputLabel>Filter by Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Filter by Status"
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="accepted">Accepted</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                  <MenuItem value="removed">Removed</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 }, width: { xs: '100%', sm: 'auto' } }}>
                <InputLabel>Filter by Shift</InputLabel>
                <Select
                  value={shiftFilter}
                  onChange={(e) => setShiftFilter(e.target.value)}
                  label="Filter by Shift"
                >
                  <MenuItem value="all">All Shifts</MenuItem>
                  {shiftOptions.map((shift) => (
                    <MenuItem key={shift} value={shift}>
                      {shift}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                placeholder="Search by phone..."
                value={phoneSearch}
                onChange={(e) => {
                  // Only allow numeric input
                  const value = e.target.value.replace(/\D/g, '')
                  setPhoneSearch(value)
                }}
                size="small"
                sx={{ minWidth: { xs: '100%', sm: 200 }, width: { xs: '100%', sm: 'auto' } }}
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ flexGrow: { xs: 0, sm: 1 }, width: { xs: '100%', sm: 'auto' } }} />
              <PrimaryButton
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setAddDialogOpen(true)}
                fullWidth={isMobile}
                sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
              >
                Add to Waitlist
              </PrimaryButton>
            </Toolbar>
            <TableContainer 
              component={Paper} 
              variant="outlined"
              sx={{ 
                maxHeight: { xs: 'calc(100vh - 300px)', sm: 'none' }, 
                overflowX: { xs: 'auto', sm: 'visible' },
                overflowY: { xs: 'auto', sm: 'visible' },
                '&::-webkit-scrollbar': {
                  height: { xs: '8px', sm: '0px' },
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '4px',
                },
              }}
            >
              <Table stickyHeader sx={{ minWidth: { xs: 800, sm: 'auto' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                    }}>
                      <TableSortLabel
                        active={orderBy === 'student_name' && order !== null}
                        direction={orderBy === 'student_name' && order !== null ? order : 'asc'}
                        onClick={() => handleRequestSort('student_name')}
                      >
                        Student Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                      display: { xs: 'none', md: 'table-cell' },
                    }}>
                      <TableSortLabel
                        active={orderBy === 'desired_shift' && order !== null}
                        direction={orderBy === 'desired_shift' && order !== null ? order : 'asc'}
                        onClick={() => handleRequestSort('desired_shift')}
                      >
                        Desired Shift
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                    }}>
                      <TableSortLabel
                        active={orderBy === 'guardian_name' && order !== null}
                        direction={orderBy === 'guardian_name' && order !== null ? order : 'asc'}
                        onClick={() => handleRequestSort('guardian_name')}
                      >
                        Guardian Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                      display: { xs: 'none', lg: 'table-cell' },
                    }}>
                      <TableSortLabel
                        active={orderBy === 'guardian_phone' && order !== null}
                        direction={orderBy === 'guardian_phone' && order !== null ? order : 'asc'}
                        onClick={() => handleRequestSort('guardian_phone')}
                      >
                        Guardian Phone
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                    }}>
                      <TableSortLabel
                        active={orderBy === 'status' && order !== null}
                        direction={orderBy === 'status' && order !== null ? order : 'asc'}
                        onClick={() => handleRequestSort('status')}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                      display: { xs: 'none', sm: 'table-cell' },
                    }}>
                      <TableSortLabel
                        active={orderBy === 'created_at' && order !== null}
                        direction={orderBy === 'created_at' && order !== null ? order : 'asc'}
                        onClick={() => handleRequestSort('created_at')}
                      >
                        Created At
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                    }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
              <TableBody>
                {paginatedEntries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isMobile ? 4 : 7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {filteredEntries.length === 0 
                          ? 'No waitlist entries found.'
                          : 'No entries on this page.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedEntries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            {entry.student_id && entry.students
                              ? `${entry.students.first_name} ${entry.students.last_name}`
                              : entry.first_name && entry.last_name
                              ? `${entry.first_name} ${entry.middle_name || ''} ${entry.last_name}`.trim()
                              : 'Unknown'}
                          </Typography>
                          {entry.converted_at && (
                            <Chip label="Converted" size="small" color="success" sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' }, height: { xs: '18px', sm: '24px' } }} />
                          )}
                        </Box>
                        {/* Show shift on mobile */}
                        {isMobile && (
                          <Box sx={{ mt: 0.5 }}>
                            {(entry.desired_shifts || (entry.desired_shift ? [entry.desired_shift] : []))
                              .filter(Boolean)
                              .map(shift => (
                                <Chip key={shift} label={shift} size="small" sx={{ mr: 0.5, mb: 0.5, fontSize: '0.65rem', height: '18px' }} />
                              ))}
                          </Box>
                        )}
                        {/* Show phone on mobile */}
                        {isMobile && entry.guardians?.phone && (
                          <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}>
                            {formatPhoneNumber(entry.guardians.phone)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ 
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        display: { xs: 'none', md: 'table-cell' },
                      }}>
                        {(entry.desired_shifts || (entry.desired_shift ? [entry.desired_shift] : []))
                          .filter(Boolean)
                          .map(shift => (
                            <Chip key={shift} label={shift} size="small" sx={{ mr: 0.5, mb: 0.5, fontSize: { xs: '0.65rem', sm: '0.75rem' } }} />
                          ))}
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          {entry.guardians
                            ? `${entry.guardians.first_name} ${entry.guardians.last_name}`
                            : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ 
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        display: { xs: 'none', lg: 'table-cell' },
                      }}>
                        {formatPhoneNumber(entry.guardians?.phone)}
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        <Chip
                          label={entry.status}
                          color={getStatusColor(entry.status)}
                          size="small"
                          sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, height: { xs: '20px', sm: '24px' } }}
                        />
                      </TableCell>
                      <TableCell sx={{ 
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        display: { xs: 'none', sm: 'table-cell' },
                      }}>
                        {format(new Date(entry.created_at), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        <Box sx={{ 
                          display: 'flex', 
                          gap: { xs: 0.5, sm: 1 }, 
                          justifyContent: 'flex-end',
                          alignItems: 'center',
                        }}>
                          {entry.student_id && (
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/student_details/${entry.student_id}`)}
                              title="View Student"
                              sx={{ padding: { xs: 0.5, sm: 1 } }}
                            >
                              <VisibilityIcon fontSize={isMobile ? 'small' : 'medium'} />
                            </IconButton>
                          )}
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            gap: { xs: 0.5, sm: 1 }, 
                            alignItems: 'flex-end',
                          }}>
                            {entry.status !== 'accepted' && (
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleUpdateStatus(entry.id, 'accepted')}
                                title="Accept"
                                sx={{ padding: { xs: 0.5, sm: 1 } }}
                              >
                                <CheckCircleIcon fontSize={isMobile ? 'small' : 'medium'} />
                              </IconButton>
                            )}
                            {entry.status !== 'rejected' && entry.status !== 'removed' && (
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleUpdateStatus(entry.id, 'rejected')}
                                title="Reject"
                                sx={{ padding: { xs: 0.5, sm: 1 } }}
                              >
                                <CancelIcon fontSize={isMobile ? 'small' : 'medium'} />
                              </IconButton>
                            )}
                            {entry.status !== 'removed' && (
                              <IconButton
                                size="small"
                                color="default"
                                onClick={() => handleRemove(entry.id)}
                                title="Remove"
                                sx={{ padding: { xs: 0.5, sm: 1 } }}
                              >
                                <DeleteIcon fontSize={isMobile ? 'small' : 'medium'} />
                              </IconButton>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredEntries.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
            }
            sx={{
              '& .MuiTablePagination-toolbar': {
                flexWrap: 'wrap',
                gap: { xs: 1, sm: 0 },
              },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontSize: { xs: '0.875rem', sm: '1rem' },
              },
            }}
          />
          </Box>
        )}
      </StyledCard>

      {/* Add to Waitlist Dialog */}
      <Dialog 
        open={addDialogOpen} 
        onClose={handleDialogClose} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            maxHeight: { xs: '100vh', sm: '90vh' },
            margin: { xs: 0, sm: 2 },
            borderRadius: { xs: 0, sm: 2 },
          },
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, pb: { xs: 1, sm: 2 } }}>
          Add Student to Waitlist
        </DialogTitle>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: { xs: 2, sm: 3 } }}>
          <Tabs 
            value={dialogTab} 
            onChange={(e, newValue) => setDialogTab(newValue)}
            variant={isMobile ? 'fullWidth' : 'standard'}
            sx={{
              '& .MuiTab-root': {
                fontSize: { xs: '0.875rem', sm: '1rem' },
                minHeight: { xs: '48px', sm: '72px' },
              },
            }}
          >
            <Tab label="Enter New Student(s)" />
            <Tab label="Select Existing Student" />
          </Tabs>
        </Box>
        <DialogContent sx={{ 
          minHeight: { xs: 'auto', sm: '500px' }, 
          maxHeight: { xs: 'calc(100vh - 200px)', sm: '70vh' }, 
          overflowY: 'auto',
          px: { xs: 2, sm: 3 },
        }}>
          <Box sx={{ minHeight: '450px' }}>
            {dialogTab === 0 ? (
              // Create New Student Tab
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  {/* Guardian Section - First */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Guardian
                    </Typography>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                      <Tabs 
                        value={guardianMode} 
                        onChange={(e, newValue) => {
                          setGuardianMode(newValue)
                          setSelectedGuardian(null)
                        }}
                        variant={isMobile ? 'fullWidth' : 'standard'}
                        sx={{ 
                          minHeight: 'auto',
                          '& .MuiTab-root': {
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            minHeight: { xs: '48px', sm: 'auto' },
                            py: { xs: 1, sm: 1 },
                          },
                        }}
                      >
                        <Tab label="Create New" value="create" />
                        <Tab label="Select Existing" value="select" />
                      </Tabs>
                    </Box>
                    {guardianMode === 'select' ? (
                      <Autocomplete
                        options={guardians}
                        getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                        value={selectedGuardian}
                        onChange={(event, newValue) => setSelectedGuardian(newValue)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Guardian"
                            required
                            fullWidth
                          />
                        )}
                      />
                    ) : (
                      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                        <CreateGuardianForm 
                          onSuccess={async (createdGuardianId) => {
                            await handleGuardianCreated()
                            if (createdGuardianId) {
                              setNewlyCreatedGuardianId(createdGuardianId)
                            }
                          }}
                        />
                      </Box>
                    )}
                  </Grid>

                  {/* Student Section - Only show after guardian is selected/created */}
                  {selectedGuardian && (
                    <>
                      {newStudents.map((student, idx) => (
                        <Grid item xs={12} key={idx} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, mt: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle1">
                              Student {idx + 1}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              {newStudents.length > 1 && (
                                <IconButton
                                  size="small"
                                  onClick={() => removeStudent(idx)}
                                  color="error"
                                >
                                  <RemoveIcon />
                                </IconButton>
                              )}
                              {idx === newStudents.length - 1 && (
                                <Button
                                  size="small"
                                  onClick={addStudent}
                                  color="primary"
                                  variant="outlined"
                                  startIcon={<AddIcon />}
                                >
                                  Add More
                                </Button>
                              )}
                            </Box>
                          </Box>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                              <TextField
                                label="First Name"
                                value={student.first_name}
                                onChange={(e) => handleStudentChange(idx, 'first_name', e.target.value)}
                                required
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <TextField
                                label="Middle Name"
                                value={student.middle_name}
                                onChange={(e) => handleStudentChange(idx, 'middle_name', e.target.value)}
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <TextField
                                label="Last Name"
                                value={student.last_name}
                                onChange={(e) => handleStudentChange(idx, 'last_name', e.target.value)}
                                required
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <FormControl fullWidth>
                                <InputLabel>Gender</InputLabel>
                                <Select
                                  value={student.gender}
                                  onChange={(e) => handleStudentChange(idx, 'gender', e.target.value)}
                                  label="Gender"
                                >
                                  <MenuItem value="Male">Male</MenuItem>
                                  <MenuItem value="Female">Female</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="Date of Birth"
                                type="date"
                                value={student.date_of_birth}
                                onChange={(e) => handleStudentChange(idx, 'date_of_birth', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={student.is_quran_student}
                                    onChange={(e) => handleStudentChange(idx, 'is_quran_student', e.target.checked)}
                                  />
                                }
                                label="Quran Student"
                              />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={student.is_greenbook_student}
                                    onChange={(e) => handleStudentChange(idx, 'is_greenbook_student', e.target.checked)}
                                  />
                                }
                                label="Greenbook Student"
                              />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={student.quran_completed}
                                    onChange={(e) => handleStudentChange(idx, 'quran_completed', e.target.checked)}
                                  />
                                }
                                label="Quran Completed"
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <FormControl fullWidth>
                                <InputLabel>Desired Shifts</InputLabel>
                                <Select
                                  multiple
                                  value={student.desired_shifts}
                                  onChange={(e) => handleStudentChange(idx, 'desired_shifts', e.target.value)}
                                  label="Desired Shifts"
                                  input={<OutlinedInput label="Desired Shifts" />}
                                  renderValue={(selected) => selected.join(', ')}
                                >
                                  {shiftOptions.map((shift) => (
                                    <MenuItem key={shift} value={shift}>
                                      <Checkbox checked={student.desired_shifts.includes(shift)} />
                                      <ListItemText primary={shift} />
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                          </Grid>
                        </Grid>
                      ))}
                    </>
                  )}
                </Grid>
              </Box>
            ) : (
              // Select Existing Student Tab
              <Box sx={{ mt: 2 }}>
                <Autocomplete
                  options={students}
                  getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                  value={selectedStudent}
                  onChange={(event, newValue) => setSelectedStudent(newValue)}
                  loading={loadingStudents}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Student"
                      required
                      fullWidth
                    />
                  )}
                />
                <Box sx={{ mt: 3 }}>
                  <FormControl fullWidth>
                    <InputLabel>Desired Shift</InputLabel>
                    <Select
                      value={desiredShift}
                      onChange={(e) => setDesiredShift(e.target.value)}
                      label="Desired Shift"
                    >
                      {shiftOptions.map((shift) => (
                        <MenuItem key={shift} value={shift}>
                          {shift}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 1 },
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          gap: { xs: 1, sm: 0 },
        }}>
          <Button 
            onClick={handleDialogClose}
            fullWidth={isMobile}
            sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
          >
            Cancel
          </Button>
          {dialogTab === 0 ? (
            <Button 
              onClick={handleCreateStudentAndAddToWaitlist} 
              variant="contained" 
              disabled={
                creatingStudent || 
                !selectedGuardian || 
                newStudents.some(s => !s.first_name.trim() || !s.last_name.trim() || s.desired_shifts.length === 0)
              }
              fullWidth={isMobile}
              sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
            >
              {creatingStudent ? <CircularProgress size={24} /> : 'Add to Waitlist'}
            </Button>
          ) : (
            <Button 
              onClick={handleAddToWaitlist} 
              variant="contained" 
              disabled={!selectedStudent || !desiredShift.trim()}
              fullWidth={isMobile}
              sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
            >
              Add to Waitlist
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Conversion Modal */}
      <WaitlistConversionModal
        open={conversionModalOpen}
        waitlistEntry={pendingConversionEntry}
        onConvert={handleConvertWaitlistEntry}
        onCancel={() => {
          setConversionModalOpen(false)
          setPendingConversionEntry(null)
        }}
      />
    </PageContainer>
  )
}

export default WaitlistPage

